/* eslint-disable */
import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { toast } from "react-hot-toast";
import DashboardCard from "../../components/dashboard/DashboardCard";
import EsimSalesGraph from "../../components/dashboard/EsimSalesGraph";
import EsimSelectedSales from "../../components/dashboard/EsimSelectedSales";
import { DashboardFilters } from "../../components/esim/DashboardFilters";
import useDashboardDatePicker from "../../hooks/useDashboardDatePicker";
import { useDebounce } from "../../hooks/useDebounce";
import useFetch from "../../hooks/useFetch";
import { IEsimPackage, ILocationZone } from "../../interfaces/esim/EsimPackages.types";
import {
  getEsimSalesOverview,
  getLocationZones,
  getPackagesByLocationZone
} from "../../services/Esim.service";

const EsimDashboard = () => {
  const { from, to, setLocalDates } = useDashboardDatePicker();
  const { loading, response, handleFunc } = useFetch(getEsimSalesOverview, {
    initRequest: false,
    loading: true,
    resetResponseOnError: true
  });

  // Location zone and package state
  const [search, setSearch] = useState("");
  const [locationZones, setLocationZones] = useState<ILocationZone[]>([]);
  const [selectedZone, setSelectedZone] = useState<ILocationZone | null>(null);
  const [packages, setPackages] = useState<IEsimPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<IEsimPackage | null>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedSourceChannel, setSelectedSourceChannel] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  // Fetch location zones
  useEffect(() => {
    const fetchLocationZones = async () => {
      try {
        const zonesResponse = await getLocationZones(debouncedSearch);
        if (zonesResponse.data.succeeded) {
          setLocationZones(zonesResponse.data.data);
        }
      } catch (error) {
        toast.error("Error fetching location zones");
      }
    };

    void fetchLocationZones();
  }, [debouncedSearch]);

  // Fetch packages when zone is selected
  useEffect(() => {
    const fetchPackages = async () => {
      if (!selectedZone) {
        setPackages([]);
        setSelectedPackage(null);
        return;
      }

      setPackagesLoading(true);
      try {
        const packagesResponse = await getPackagesByLocationZone(selectedZone.slug);
        if (packagesResponse.data) {
          setPackages(packagesResponse.data);
        }
      } catch (error) {
        toast.error("Error fetching packages");
        setPackages([]);
      } finally {
        setPackagesLoading(false);
      }
    };

    void fetchPackages();
  }, [selectedZone]);

  // Fetch sales data when filters change
  useEffect(() => {
    if (from && to) {
      void handleFunc({
        windowStart: format(parse(from, "dd/MM/yyyy", new Date()), "MM-dd-yyyy"),
        windowEnd: format(parse(to, "dd/MM/yyyy", new Date()), "MM-dd-yyyy"),
        locationZoneId: selectedZone?.id,
        packageTemplateId: selectedPackage?.id,
        sourceChannel: selectedSourceChannel || undefined
      });
    }
  }, [from, to, selectedZone, selectedPackage, selectedSourceChannel]);

  const onDateApply = (r: DateRange | undefined) => {
    if (r?.from && r?.to)
      setLocalDates({
        from: format(r.from, "dd/MM/yyyy"),
        to: format(r.to, "dd/MM/yyyy")
      });
  };

  const handleZoneSelect = (zone: ILocationZone) => {
    setSelectedZone(zone);
    setIsInputFocused(false);
    setSearch("");
    setSelectedPackage(null);
  };

  const handleZoneClear = () => {
    setSelectedZone(null);
    setSelectedPackage(null);
    setPackages([]);
    setSearch("");
  };

  const handlePackageSelect = (packageItem: IEsimPackage | null) => {
    setSelectedPackage(packageItem);
  };

  return (
    <div className="mt-6">
      {/* Sales cards */}
      <div className="flex w-full justify-between lg:flex-row flex-col mb-6">
        <DashboardCard
          title="Shitjet e sotme"
          delay={200}
          type="fade-in"
          className="mr-2.5 lg:mb-0 mb-2.5"
          currentValue={response?.data.todaySales?.currentValue || "0"}
          ratePercentage={response?.data?.todaySales?.ratePercentage || "0"}
          ratePercentageDesc="prej periudhës së kaluar"
          loading={loading}
        />
        <DashboardCard
          delay={300}
          type="fade-in"
          className="lg:mx-2.5 my-2.5 lg:my-0"
          title="Shitjet e kësaj jave"
          currentValue={response?.data.thisWeekSales?.currentValue || "0"}
          ratePercentage={response?.data?.thisWeekSales?.ratePercentage || "0"}
          ratePercentageDesc="prej periudhës së kaluar"
          loading={loading}
        />
        <DashboardCard
          delay={400}
          type="fade-in"
          title="Shitjet e 30 ditëve të kaluara"
          currentValue={response?.data.thisMonthSales?.currentValue || "0"}
          ratePercentage={response?.data?.thisMonthSales?.ratePercentage || "0"}
          ratePercentageDesc="prej periudhës së kaluar"
          loading={loading}
        />
      </div>

      {/* Graph and analytics with inline filters */}
      <div className="flex w-full flex-col">
        <DashboardFilters
          from={from}
          to={to}
          search={search}
          setSearch={setSearch}
          locationZones={locationZones}
          selectedZone={selectedZone}
          packages={packages}
          selectedPackage={selectedPackage}
          isInputFocused={isInputFocused}
          setIsInputFocused={setIsInputFocused}
          packagesLoading={packagesLoading}
          selectedSourceChannel={selectedSourceChannel}
          onDateApply={onDateApply}
          onZoneSelect={handleZoneSelect}
          onZoneClear={handleZoneClear}
          onPackageSelect={handlePackageSelect}
          onSourceChannelSelect={setSelectedSourceChannel}
        />

        <div className="flex w-full gap-6 flex-col lg:flex-row">
          <div className="w-full lg:w-1/2">
            <EsimSelectedSales
              currentValue={response?.data?.selectedSales?.currentValue || "0"}
              ratePercentage={response?.data?.selectedSales?.ratePercentage}
              loading={loading}
              selectPeriodCost={response?.data?.selectPeriodCost}
              selectedPeriodEsimCount={response?.data?.selectedPeriodEsimCount}
              selectedPeriodGbUsage={response?.data?.selectedPeriodGbUsage}
              selectedPeriodProfit={response?.data?.selectedPeriodProfit}
              selectedPeriodTopUpCount={response?.data?.selectedPeriodTopUpCount}
              selectedPeriodTotalCount={response?.data?.selectedPeriodTotalCount}
              hasLocationZoneFilter={!!selectedZone}
              hasSourceChannelFilter={selectedSourceChannel !== null && selectedSourceChannel !== ""}
            />
          </div>
          <div className="w-full lg:w-1/2">
            <EsimSalesGraph
              data={response?.data?.dailyCounts}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EsimDashboard;
